import type { Property } from '@/types/property'

export interface PropertyAreaSize {
  value: number
  unit: string
  label: string
  type: 'land' | 'carpet'
}

export function isSquareFootUnit(unit?: string | null): boolean {
  const normalized = (unit ?? '').toLowerCase().trim()
  if (!normalized) return true
  return ['sq.ft', 'sqft', 'sq ft', 'square foot', 'square feet', 'sft'].some((token) =>
    normalized.includes(token),
  )
}

export function getPropertyAreaSize(property: Pick<Property, 'areaSize' | 'sqft'>): PropertyAreaSize | null {
  if (property.areaSize?.label) {
    return property.areaSize
  }
  if (property.sqft && property.sqft > 0) {
    return {
      value: property.sqft,
      unit: 'sq.ft',
      label: `${property.sqft.toLocaleString('en-IN')} sq.ft`,
      type: 'carpet',
    }
  }
  return null
}

export function formatPropertyArea(property: Pick<Property, 'areaSize' | 'sqft'>): string {
  return getPropertyAreaSize(property)?.label ?? ''
}

export function propertyAreaHeading(property: Pick<Property, 'areaSize'>): string {
  if (property.areaSize?.type === 'land') return 'Land Area'
  return 'Built-up Area'
}

export function shouldShowPricePerSqft(property: Pick<Property, 'areaSize' | 'sqft' | 'price'>): boolean {
  const area = getPropertyAreaSize(property)
  if (!area) return false
  if (!isSquareFootUnit(area.unit)) return false
  const price = typeof property.price === 'number' ? property.price : parseFloat(String(property.price).replace(/,/g, ''))
  return Number.isFinite(price) && price > 10000 && area.value > 0
}

/** True when listing has a real bedroom/BHK label (hide bed icon for commercial/plots). */
export function hasBedroomLabel(bhk?: string | null): boolean {
  const value = (bhk ?? '').trim()
  if (!value || value === '-' || value === '0' || /^n\/?a$/i.test(value)) return false
  return /\d/.test(value) || /bhk/i.test(value)
}
